import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from "rxjs";
import {HttpHeaders} from "@angular/common/http";
import {finalize, shareReplay, tap} from "rxjs/operators";
import {ConversationRestService} from "../../api/services/conversation.rest.service";
import {MessageRestService, CreateMessagePayload} from "../../api/services/message.rest.service";
import {ResultListApiModel} from "../../api/models/result-list.api.model";
import {ConversationApiModel} from "../../api/models/conversation.api.model";
import {ChatMessageApiModel} from "../../api/models/chat-message.api.model";
import {QueryParamsApiModel} from "../../api/models/query-params-api.model";
import {FilterParameterApiModel} from "../../api/models/filter-parameter.api.model";
import {EventSourceService} from "../../core/services/event-source.service";
import {EventSourceInterface} from "../../core/interfaces/event-source.interface";

@Injectable({
  providedIn: 'root'
})
export class ChatDataProviderService {
  private conversationsCache: ResultListApiModel<ConversationApiModel> | null = null;
  private messagesCache = new Map<string, ResultListApiModel<ChatMessageApiModel>>();
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public readonly unreadCount$ = this.unreadCountSubject.asObservable();
  private unreadCountRequest$: Observable<number> | null = null;

  constructor(
    private conversationRS: ConversationRestService,
    private messageRS: MessageRestService,
    private eventSource: EventSourceService
  ) {
  }

  public getConversations(
    page: number = 1,
    limit: number = 20,
    useCache: boolean = true,
    forceRefresh: boolean = false,
    softRefresh: boolean = false
  ): Observable<ResultListApiModel<ConversationApiModel>> {
    const params = new QueryParamsApiModel([], page, 'DESC', limit);
    if (page === 1 && useCache && this.conversationsCache) {
      return of(this.conversationsCache);
    }
    let headers: HttpHeaders | undefined;
    if (forceRefresh) {
      headers = new HttpHeaders({'x-cache-refresh': 'true'});
    } else if (softRefresh) {
      headers = new HttpHeaders({'x-cache-soft-refresh': 'true'});
    }
    return this.conversationRS.getCollection(params, headers).pipe(tap((result) => {
      if (page === 1) {
        this.conversationsCache = result;
      }
    }));
  }

  public updateConversationsCache(result: ResultListApiModel<ConversationApiModel>): void {
    this.conversationsCache = result;
  }

  public updateConversationsCacheFromRecords(records: ConversationApiModel[], total: number): void {
    if (this.conversationsCache) {
      this.conversationsCache.records = records;
      this.conversationsCache.total = total;
      return;
    }
    this.conversationsCache = new ResultListApiModel<ConversationApiModel>(ConversationApiModel);
    this.conversationsCache.records = records;
    this.conversationsCache.total = total;
  }

  public hasConversationsCache(): boolean {
    return !!this.conversationsCache?.records?.length;
  }

  public getConversationsCache(): ResultListApiModel<ConversationApiModel> | null {
    return this.conversationsCache;
  }

  public getConversationFromCacheById(id: string): ConversationApiModel | null {
    if (!this.conversationsCache?.records?.length) {
      return null;
    }
    return this.conversationsCache.records.find((conversation) => {
      const iri = conversation?.['@id'];
      if (!iri) {
        return false;
      }
      if (iri.endsWith(`/conversations/${id}`) || iri.endsWith(`/api/conversations/${id}`)) {
        return true;
      }
      return iri.split('/').pop() === id;
    }) || null;
  }

  public updateMessagesCacheFromRecords(
    conversationIri: string,
    records: ChatMessageApiModel[],
    total: number
  ): void {
    const cacheKey = this.normalizeConversationKey(conversationIri);
    const existing = this.messagesCache.get(cacheKey);
    if (existing) {
      existing.records = records;
      existing.total = total;
      return;
    }
    const result = new ResultListApiModel<ChatMessageApiModel>(ChatMessageApiModel);
    result.records = records;
    result.total = total;
    this.messagesCache.set(cacheKey, result);
  }

  public clearMessagesCache(conversationIri: string): void {
    this.messagesCache.delete(this.normalizeConversationKey(conversationIri));
  }

  public hasMessagesCache(conversationIri: string): boolean {
    const cached = this.messagesCache.get(this.normalizeConversationKey(conversationIri));
    return !!cached?.records?.length;
  }

  public getMessagesCache(conversationIri: string): ResultListApiModel<ChatMessageApiModel> | null {
    return this.messagesCache.get(this.normalizeConversationKey(conversationIri)) || null;
  }

  public appendMessageToCache(conversationIri: string, message: ChatMessageApiModel): void {
    if (!message) {
      return;
    }
    const cacheKey = this.normalizeConversationKey(conversationIri || message.conversation);
    if (!cacheKey) {
      return;
    }
    const existing = this.messagesCache.get(cacheKey);
    if (!existing) {
      const result = new ResultListApiModel<ChatMessageApiModel>(ChatMessageApiModel);
      result.records = [message];
      result.total = 1;
      this.messagesCache.set(cacheKey, result);
      return;
    }
    const records = existing.records || [];
    const messageKey = this.getMessageCacheKey(message);
    if (records.find((item) => this.getMessageCacheKey(item) === messageKey)) {
      return;
    }
    const nextRecords = [...records, message].sort((a, b) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });
    existing.records = nextRecords;
    existing.total = Math.max(existing.total || 0, nextRecords.length);
  }

  public getConversation(conversationIri: string): Observable<ConversationApiModel> {
    return this.conversationRS.get(conversationIri);
  }

  public getMessages(
    conversationIri: string,
    page: number = 1,
    limit: number = 30,
    order: 'ASC'|'DESC' = 'DESC',
    useCache: boolean = true,
    forceRefresh: boolean = false,
    softRefresh: boolean = false
  ): Observable<ResultListApiModel<ChatMessageApiModel>> {
    const conversationId = this.normalizeConversationId(conversationIri);
    const params = new QueryParamsApiModel([
      new FilterParameterApiModel('conversation.id', conversationId, 'single'),
      new FilterParameterApiModel('order[createdAt]', order, 'single')
    ], page, null, limit);

    if (page === 1 && useCache) {
      const cached = this.messagesCache.get(this.normalizeConversationKey(conversationIri));
      if (cached) {
        return of(cached);
      }
    }

    let headers: HttpHeaders | undefined;
    if (forceRefresh) {
      headers = new HttpHeaders({'x-cache-refresh': 'true'});
    } else if (softRefresh) {
      headers = new HttpHeaders({'x-cache-soft-refresh': 'true'});
    }
    return this.messageRS.getCollection(params, headers).pipe(tap((result) => {
      if (page === 1) {
        this.messagesCache.set(this.normalizeConversationKey(conversationIri), result);
      }
    }));
  }

  private normalizeConversationId(conversationIri: string): string {
    if (!conversationIri) {
      return '';
    }
    if (conversationIri.includes('/')) {
      return conversationIri.split('/').filter(Boolean).pop() || conversationIri;
    }
    return conversationIri;
  }

  public shouldUseMessagesCache(conversationIri: string, lastMessageAt?: Date | string): boolean {
    if (!lastMessageAt) {
      return true;
    }
    const cached = this.messagesCache.get(this.normalizeConversationKey(conversationIri));
    if (!cached?.records?.length) {
      return false;
    }
    const lastCached = cached.records[cached.records.length - 1];
    if (!lastCached?.createdAt) {
      return false;
    }
    return new Date(lastCached.createdAt) >= new Date(lastMessageAt);
  }

  private normalizeConversationKey(conversationIri: string): string {
    if (!conversationIri) {
      return '';
    }
    const parts = conversationIri.split('/').filter(Boolean);
    const id = parts[parts.length - 1] || conversationIri;
    return `/conversations/${id}`;
  }

  public createMessage(payload: CreateMessagePayload): Observable<ChatMessageApiModel> {
    return this.messageRS.create(payload);
  }

  public markRead(conversationId: string, lastMessage?: string): Observable<ConversationApiModel> {
    return this.conversationRS.markRead(conversationId, lastMessage);
  }

  public touchPresence(conversationId: string): Observable<void> {
    return this.conversationRS.touchPresence(conversationId);
  }

  public clearPresence(): Observable<void> {
    return this.conversationRS.clearPresence();
  }

  public listenConversationMessages(conversationId: string): Observable<EventSourceInterface<ChatMessageApiModel>> {
    return this.eventSource.listenSingleTopic<ChatMessageApiModel>(`/conversations/${conversationId}/messages`);
  }

  public listenConversationList(userId: string, closable: boolean = true): Observable<EventSourceInterface<ConversationApiModel>> {
    return this.eventSource.listenSingleTopic<ConversationApiModel>(`/users/${userId}/conversations`, closable);
  }

  public listenUnreadCount(userId: string, closable: boolean = true): Observable<EventSourceInterface<{count: number}>> {
    return this.eventSource.listenSingleTopic<{count: number}>(`/users/${userId}/unread-count`, closable);
  }

  public getUnreadCount(): Observable<number> {
    if (this.unreadCountRequest$) {
      return this.unreadCountRequest$;
    }

    this.unreadCountRequest$ = this.conversationRS.getUnreadCount().pipe(
      tap((count) => this.unreadCountSubject.next(count)),
      finalize(() => {
        this.unreadCountRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.unreadCountRequest$;
  }

  public setUnreadCount(count: number): void {
    if (typeof count !== 'number') {
      return;
    }
    this.unreadCountSubject.next(count);
  }

  public updateConversationPreview(conversationIri: string, message: ChatMessageApiModel): void {
    if (!conversationIri || !message || !this.conversationsCache?.records?.length) {
      return;
    }
    const index = this.conversationsCache.records.findIndex((conversation) => conversation?.['@id'] === conversationIri);
    if (index < 0) {
      return;
    }
    const existing = this.conversationsCache.records[index];
    const next = {
      ...existing,
      lastMessage: message,
      lastMessageAt: message.createdAt || new Date()
    } as ConversationApiModel;
    this.conversationsCache.records.splice(index, 1);
    this.conversationsCache.records.unshift(next);
  }

  private getMessageCacheKey(message: ChatMessageApiModel): string {
    if (!message) {
      return '';
    }
    if (message['@id'] || message.clientTempId || message.id) {
      return message['@id'] || message.clientTempId || message.id || '';
    }
    const senderId = typeof message.sender === 'string'
      ? message.sender
      : message.sender?.id?.toString() || '';
    const createdAt = message?.createdAt ? new Date(message.createdAt).getTime() : 0;
    const attachmentCount = Array.isArray(message?.attachments) ? message.attachments.length : 0;
    const content = message?.content || '';
    return `${senderId}|${createdAt}|${content}|${attachmentCount}`;
  }
}
